import { FEATURES, MODAL, SEARCH_GROUPS } from '@/src/constants/Text'
import { lazyLoadView } from '@utils/LazyLoad'
import store from '@state/store'
import { isWelcomeDismissed } from '@modules/design/utils/welcome-storage'

// Quem tem a feature e já viu o welcome (marcado automaticamente na 1ª visita)
// vai direto para a overview; os demais (inclusive público de cross-sell) caem
// no welcome, que segue acessível pela aba "Bem-vindo".
const resolveComplianceRedirect = () => {
  const show = store.getters['parameters/show'] || {}
  if (show[FEATURES.COMPLIANCE] && isWelcomeDismissed('compliance')) {
    return { name: 'compliance.overview' }
  }
  return { name: 'compliance.welcome' }
}

export default [
  {
    path: '/compliance',
    name: 'compliance',
    // customPermission: libera acesso se o escritório tem FEATURES.COMPLIANCE OU se não tem OCULTAR_CROSS_SELL
    // (mantém a página de boas-vindas acessível como upsell para escritórios sem a contratação).
    meta: {
      customPermission: ({ show }) =>
        !!show[FEATURES.COMPLIANCE] || !show[FEATURES.OCULTAR_CROSS_SELL],
      search: {
        title: 'Compliance',
        description: 'IPS, contratos, KYC e formulários de clientes',
        keywords: ['compliance', 'ips', 'contratos', 'kyc', 'regulatório'],
        icon: 'book-cover',
        group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
        // Busca global: vai direto à Visão Geral (mesmo destino pós-welcome do
        // resolveComplianceRedirect) para quem acessa a overview; sem
        // FEATURES.COMPLIANCE, a entrada segue para a raiz → Bem-vindo (upsell).
        landing: 'compliance.overview',
      },
    },
    redirect: resolveComplianceRedirect,

    component: () =>
      lazyLoadView(
        import('@modules/compliance/views/nav-bar/compliance-nav-bar.vue')
      ),
    children: [
      {
        path: 'bem-vindo',
        name: 'compliance.welcome',
        // customPermission: welcome é acessível como upsell quando OCULTAR_CROSS_SELL está ausente;
        // retorna 404 apenas quando OCULTAR_CROSS_SELL=true e o escritório não tem FEATURES.COMPLIANCE.
        meta: {
          customPermission: ({ show }) =>
            !!show[FEATURES.COMPLIANCE] || !show[FEATURES.OCULTAR_CROSS_SELL],
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/compliance/views/welcome/view-compliance-welcome.vue'
            )
          ),
      },
      {
        path: 'visao-geral',
        name: 'compliance.overview',
        component: () =>
          lazyLoadView(
            import(
              '@modules/compliance/views/overview/view-compliance-overview.vue'
            )
          ),
        meta: {
          can_show: FEATURES.COMPLIANCE,
          isCompliance: true,
          search: {
            title: 'Compliance — Visão Geral',
            description: 'Panorama de conformidade e pendências do escritório',
            keywords: ['visão geral', 'panorama', 'conformidade', 'resumo'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
      },
      {
        path: 'ips',
        name: 'compliance.ips',
        redirect: '/compliance/ips/',
        component: () =>
          lazyLoadView(
            import(
              '@modules/compliance/views/ips-navigation/view-ips-navigation.vue'
            )
          ),
        meta: {
          can_show: FEATURES.COMPLIANCE,
          isCompliance: true,
          search: {
            title: 'IPS',
            description: 'Política de investimento (IPS) dos clientes',
            keywords: [
              'ips',
              'política de investimento',
              'suitability',
              'perfil',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        children: [
          {
            path: '',
            name: 'compliance.ips.home',
            // can_show: bloqueia acesso via URL para escritórios sem a flag
            meta: {
              can_show: FEATURES.COMPLIANCE,
              isCompliance: true,
            },
            component: () =>
              lazyLoadView(
                import('@modules/compliance/views/ips/view-ips.vue')
              ),
          },
          {
            path: 'configuracoes',
            name: 'compliance.ips.settings',
            // can_show: bloqueia acesso via URL para escritórios sem a flag
            meta: {
              can_show: FEATURES.COMPLIANCE,
              isCompliance: true,
              search: {
                title: 'Compliance — Configurações de IPS',
                description:
                  'Alocações e parâmetros da política de investimento',
                keywords: [
                  'ips',
                  'configurações',
                  'alocação',
                  'perfil',
                  'suitability',
                ],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/settings/views/asset-allocation/view-asset-allocation-ips.vue'
                )
              ),
          },
        ],
      },
      {
        path: 'contratos',
        name: 'compliance.contracts',
        redirect: '/compliance/contratos/',
        component: () =>
          lazyLoadView(
            import(
              '@modules/compliance/views/documents-navigation/view-documents-navigation.vue'
            )
          ),
        meta: {
          can_show: FEATURES.COMPLIANCE,
          isCompliance: true,
          search: {
            title: 'Contratos',
            description: 'Contratos, modelos e kit de documentos',
            keywords: [
              'contratos',
              'documentos',
              'modelos',
              'kit',
              'assinatura',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
            actions: [
              {
                id: 'importar-contratos-lote',
                title: 'Importar contratos em lote',
                keywords: ['importar', 'lote', 'contratos', 'jurídico'],
                to: { name: 'compliance.contracts' },
                modal: MODAL.IMPORT_BATCH_JURIDICO,
              },
            ],
          },
        },
        children: [
          {
            path: '',
            name: 'compliance.contract.overview',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/compliance/views/contracts/view-compliance-contracts.vue'
                )
              ),
            // can_show: bloqueia acesso via URL para escritórios sem a flag
            meta: {
              can_show: FEATURES.COMPLIANCE,
              isCompliance: true,
            },
          },
          {
            path: 'modelos',
            name: 'compliance.contract.models',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/compliance/views/models-contracts/view-models-contracts.vue'
                )
              ),
            // can_show: bloqueia acesso via URL para escritórios sem a flag
            meta: {
              can_show: FEATURES.COMPLIANCE,
              isCompliance: true,
              search: {
                title: 'Compliance — Modelos de Contrato',
                description: 'Modelos de contrato usados na geração de kits',
                keywords: ['modelos', 'contrato', 'template', 'documentos'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
                actions: [
                  {
                    id: 'novo-modelo-contrato',
                    title: 'Novo modelo de contrato',
                    keywords: ['novo', 'modelo', 'contrato', 'criar'],
                    to: { name: 'compliance.contract.models' },
                    modal: MODAL.DOCS_SAVE_TEMPLATES,
                  },
                ],
              },
            },
          },
          {
            path: 'kit',
            name: 'compliance.contract.kit',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/compliance/views/kit-documentos/view-kit-documentos.vue'
                )
              ),
            meta: {
              can_show: FEATURES.COMPLIANCE,
              isCompliance: true,
              search: {
                title: 'Compliance — Kit de Documentos',
                description: 'Kits de documentos montados a partir dos modelos',
                keywords: ['kit', 'documentos', 'contrato', 'assinatura'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
        ],
      },
      {
        path: 'formularios-clientes',
        name: 'compliance.clients-forms',
        redirect: '/compliance/formularios-clientes/',
        component: () =>
          lazyLoadView(
            import(
              '@modules/compliance/views/form-navigation/view-form-navigation.vue'
            )
          ),
        meta: {
          can_show: FEATURES.COMPLIANCE,
          isCompliance: true,
          search: {
            title: 'Formulários de Clientes',
            description: 'Formulários e configurações de dados de clientes',
            keywords: ['formulários', 'clientes', 'cadastro', 'dados'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
            actions: [
              {
                id: 'cadastrar-cliente',
                // Título distinto da ação "Cadastrar cliente" de Clientes
                // (src/router/routes.js, MODAL.CADASTRAR_CLIENTES): com o mesmo
                // título, a do CRM/Clientes vinha antes e o Enter abria o modal
                // errado (review do PR #2209). Keywords sem "cliente"/"novo"
                // para não competir com as de lá.
                title: 'Cadastrar cliente no Compliance',
                keywords: ['compliance', 'formulário de cliente', 'kyc'],
                to: { name: 'compliance.clients-forms' },
                modal: MODAL.COMPLIANCE_CADASTRAR_CLIENTES,
                // Handler openRegisterClient(v) faz busModal.$emit('dialog', v):
                // sem payload, v vem undefined e o diálogo não abre.
                modalPayload: true,
              },
            ],
          },
        },
        children: [
          {
            path: '',
            name: 'compliance.form.home',
            meta: {
              can_show: FEATURES.COMPLIANCE,
              isCompliance: true,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/compliance/views/clients/view-compliance-clients.vue'
                )
              ),
          },
          {
            path: 'configuracoes',
            name: 'compliance.form.settings',
            // can_show: bloqueia acesso via URL para escritórios sem a flag
            meta: {
              can_show: FEATURES.COMPLIANCE,
              isCompliance: true,
              search: {
                title: 'Compliance — Configurações de Formulários',
                description: 'Configuração dos formulários enviados a clientes',
                keywords: ['formulários', 'configurações', 'clientes', 'dados'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/compliance/components/form-config/compliance-form-config.vue'
                )
              ),
          },
        ],
      },

      {
        path: 'kyc',
        name: 'compliance.kyc',
        component: () =>
          lazyLoadView(
            import('@modules/compliance/views/kyc/view-compliance-kyc.vue')
          ),
        meta: {
          can_show: FEATURES.COMPLIANCE,
          isCompliance: true,
          search: {
            title: 'KYC',
            description: 'Know Your Client — conheça seu cliente',
            keywords: [
              'kyc',
              'conheça seu cliente',
              'cadastro',
              'due diligence',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
      },
    ],
  },
]
